import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form } from '@bancobarigui/barihaus';

import { providerCompanyService } from '@/services/providerCompany.service';
import { useErrorHandler } from '@/shared/hooks/useErrorHandler';
import { useAlertContext } from '@/contexts/alert.context';

export function useProviderCompanyEdit() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [originalProvider, setOriginalProvider] = useState<any>(null);

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { handleError } = useErrorHandler();
  const { notify } = useAlertContext();

  const loadProviderData = async () => {
    if (!id) {
      notify({
        type: 'error',
        message: 'ID da prestadora não fornecido',
      });
      navigate('/vendor-list');
      return;
    }

    setLoading(true);
    try {
      const provider = await providerCompanyService.getProviderCompanyById(id);
      setOriginalProvider(provider);

      form.setFieldsValue({
        cnpj: provider.cnpj,
        integrationCode: provider.integrationCode,
        name: provider.name,
        shortName: provider.shortName,
        email: provider.email,
        phone: provider.phones?.[0]?.phoneNumber || '',
        phoneType: provider.phones?.[0]?.phoneType || 'CELULAR',
        postalCode: provider.postalCode,
        street: provider.street,
        number: provider.number,
        complement: provider.complement,
        district: provider.district,
        state: provider.state,
        city: provider.city,
        allowsDemandReceiving: provider.allowsDemandReceiving,
        inspectionTypes: provider.inspectionTypes?.map((it: any) => it.inspectionTypeId) || [],
        propertyTypes: provider.propertyTypes?.map((pt: any) => pt.propertyTypeId) || [],
        minValue: provider.minValue ? provider.minValue.toString() : '',
        maxValue: provider.maxValue ? provider.maxValue.toString() : '',
      });
    } catch (error) {
      handleError(error, 'Erro ao carregar dados da prestadora');
      navigate('/vendor-list');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    if (!id) return;

    setSubmitting(true);
    try {
      // Use valores originais como fallback se os arrays estiverem vazios
      const inspectionTypeIds = values.inspectionTypes && values.inspectionTypes.length > 0
        ? values.inspectionTypes
        : (originalProvider?.inspectionTypes?.map((it: any) => it.inspectionTypeId) || []);

      const propertyTypeIds = values.propertyTypes && values.propertyTypes.length > 0
        ? values.propertyTypes
        : (originalProvider?.propertyTypes?.map((pt: any) => pt.propertyTypeId) || []);

      const payload = {
        cnpj: values.cnpj,
        integrationCode: values.integrationCode || '',
        name: values.name,
        shortName: values.shortName,
        email: values.email,
        phones: [{ phoneNumber: values.phone, phoneType: values.phoneType }],
        postalCode: values.postalCode.replace(/\./g, '').replace(/-/g, ''),
        street: values.street,
        number: values.number ?? '',
        complement: values.complement ?? '',
        district: values.district,
        state: values.state,
        city: values.city,
        allowsDemandReceiving: values.allowsDemandReceiving ?? false,
        isActive: true,
        minValue: Number(values.minValue?.replace(/\D/g, '')) || 0,
        maxValue: Number(values.maxValue?.replace(/\D/g, '')) || 0,
        inspectionTypeIds,
        propertyTypeIds,
      };

      await providerCompanyService.updateProviderCompany(id, payload);

      notify({
        type: 'success',
        message: 'Prestadora atualizada com sucesso!',
      });
      navigate('/vendor-list');
    } catch (error) {
      handleError(error, 'Erro ao atualizar prestadora');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/vendor-list');
  };

  useEffect(() => {
    loadProviderData();
  }, [id]);

  return {
    form,
    loading,
    submitting,
    handleSubmit,
    handleCancel,
  };
}
